/**
 * GitHub OAuth Service
 *
 * Handles GitHub OAuth authentication via the Logic Apps automation
 * endpoint using Bearer token auth.
 */

import { getLogicEndpoint, apiVersions } from '@/services/endpoints';
import {
  getWorkflowAssistantAffinityHeader,
  captureWorkflowAssistantAffinity,
} from '@/services/workflowAssistantAffinity';

const GITHUB_CLIENT_ID = import.meta.env.VITE_GITHUB_CLIENT_ID;
const OAUTH_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export interface GitHubUser {
  login: string;
  name: string;
  avatarUrl: string;
  id: number;
}

interface GitHubAuthStatusResponse {
  authenticated: boolean;
  user?: GitHubUser;
}

interface GitHubTokenResponse {
  tokenType: string;
  user: GitHubUser;
}

function validateParams(projectName: string, accessToken: string): void {
  if (!projectName.trim()) throw new Error('Project name is required');
  if (!accessToken.trim()) throw new Error('Access token is required');
}

/**
 * Check if the user has an active GitHub session.
 */
export async function checkGitHubAuthStatus(projectName: string, accessToken: string): Promise<GitHubAuthStatusResponse> {
  validateParams(projectName, accessToken);

  const response = await fetch(
    `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/getGithubAuthStatus?api-version=${apiVersions.otto}`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        ...getWorkflowAssistantAffinityHeader(),
      },
    },
  );
  captureWorkflowAssistantAffinity(response);
  if (!response.ok) {
    throw new Error(`GitHub auth status check failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Exchange a GitHub OAuth authorization code for a session.
 */
export async function exchangeGitHubCode(projectName: string, accessToken: string, code: string, redirectUri: string): Promise<GitHubUser> {
  validateParams(projectName, accessToken);

  const response = await fetch(
    `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/getGithubToken?api-version=${apiVersions.otto}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...getWorkflowAssistantAffinityHeader(),
      },
      body: JSON.stringify({ code, redirectUri }),
    },
  );
  captureWorkflowAssistantAffinity(response);
  if (!response.ok) {
    throw new Error(`GitHub code exchange failed: ${response.status}`);
  }
  const data: GitHubTokenResponse = await response.json();
  return data.user;
}

/**
 * Clear the GitHub session.
 */
export async function logoutGitHub(projectName: string, accessToken: string): Promise<void> {
  validateParams(projectName, accessToken);

  const response = await fetch(
    `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/workflowAssistants/default/purgeGithubToken?api-version=${apiVersions.otto}`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        ...getWorkflowAssistantAffinityHeader(),
      },
    },
  );
  captureWorkflowAssistantAffinity(response);
  if (!response.ok) {
    throw new Error(`GitHub logout failed: ${response.status}`);
  }
}

/**
 * Open a popup to GitHub's OAuth authorization page.
 * Returns a Promise that resolves with the authenticated user
 * once the callback page posts back via postMessage.
 * Rejects on popup blocked, user close, or timeout.
 */
export function openGitHubLogin(): Promise<GitHubUser> {
  return new Promise<GitHubUser>((resolve, reject) => {
    if (!GITHUB_CLIENT_ID) {
      reject(new Error('VITE_GITHUB_CLIENT_ID is not configured'));
      return;
    }

    // CSRF protection: random state parameter verified by callback page
    const state = crypto.randomUUID();
    sessionStorage.setItem('github_oauth_state', state);

    const redirectUri = `${window.location.origin}/github/callback`;
    const authUrl =
      `https://github.com/login/oauth/authorize` +
      `?client_id=${GITHUB_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=read:user` +
      `&state=${state}`;

    const width = 600;
    const height = 700;
    const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);

    const popup = window.open(
      authUrl,
      'github-oauth',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`,
    );

    if (!popup) {
      reject(new Error('Popup blocked — please allow popups for this site'));
      return;
    }

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(pollTimer);
      clearTimeout(timeoutTimer);
      sessionStorage.removeItem('github_oauth_state');
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'github-auth-success') return;

      const user = event.data.user;
      if (!user?.login || !user?.id) return; // shape validation

      cleanup();
      resolve(user as GitHubUser);
    };

    window.addEventListener('message', onMessage);

    // Poll for popup closure (user closed without completing)
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error('GitHub login popup was closed'));
      }
    }, 1000);

    // Timeout: reject if OAuth flow takes too long
    const timeoutTimer = setTimeout(() => {
      cleanup();
      popup.close();
      reject(new Error('GitHub login timed out — please try again'));
    }, OAUTH_TIMEOUT_MS);
  });
}
